import React, { useMemo } from "react";
import {
  ChartModalShell,
  StackedLevelBars,
  CwEvents,
} from "../screens/logs/LogChartParts";
import { bucketByLevel, levelTotals, LEVEL_STATUS } from "../utility/logAnalytics";
import { windowMs } from "../screens/logs/logSources";

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// LogVolumeChartModal (#247) — repurposed from the dormant "User Activity" chart.
//
// Shows log VOLUME over time, stacked by status level (error / warn / info), for
// the selected Lambda's log group. One count axis, legend, status colors
// (reserved, always labeled). Answers "when did the noise / errors spike?"
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

function Totals({ totals }) {
  return (
    <div className="flex gap-4 text-xs mb-2">
      {["error", "warn", "info"].map((lvl) => (
        <span key={lvl} className="flex items-center gap-1">
          <span
            style={{
              display: "inline-block",
              width: 10,
              height: 10,
              borderRadius: 2,
              backgroundColor: LEVEL_STATUS[lvl].color,
            }}
          />
          {LEVEL_STATUS[lvl].label}: <b>{totals[lvl]}</b>
        </span>
      ))}
    </div>
  );
}

function Chart({ events, loading, startMs, endMs, note }) {
  const data = useMemo(
    () =>
      bucketByLevel(events, {
        startMs,
        endMs,
        buckets: 24,
        timeOf: (e) => e.timestamp ?? e.receivedAt,
        levelOf: (e) => e.level,
      }),
    [events, startMs, endMs]
  );
  const totals = useMemo(() => levelTotals(events, (e) => e.level), [events]);
  if (loading) return <div className="text-skin-link italic p-4">Loading…</div>;
  return (
    <div className="flex flex-col h-full min-h-0">
      <Totals totals={totals} />
      {note && <div className="text-xs text-skin-link mb-1">{note}</div>}
      <div className="flex-1 min-h-0">
        <StackedLevelBars data={data} />
      </div>
    </div>
  );
}

export const LogVolumeChartModal = ({ isOpen, onClose, source, cwWindowId }) => {
  // Re-anchor the time window each time the modal opens (intentional isOpen dep).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const anchorMs = useMemo(() => Date.now(), [isOpen]);
  if (!isOpen) return null;

  const startMs = anchorMs - windowMs(cwWindowId);

  return (
    <ChartModalShell
      title="📊 Log volume by level"
      subtitle={source?.label || ""}
      onClose={onClose}
    >
      <CwEvents
        groupId={source?.cwGroupId}
        windowId={cwWindowId}
        anchorMs={anchorMs}
        render={(events, loading, truncated, error) => (
          <Chart
            events={events}
            loading={loading}
            startMs={startMs}
            endMs={anchorMs}
            note={
              error
                ? `CloudWatch error: ${error}`
                : truncated
                ? "This window holds more events than the chart fetches. Showing the OLDEST ones — narrow the window to see current activity."
                : ""
            }
          />
        )}
      />
    </ChartModalShell>
  );
};
